import { ItemIcon } from '@sorrem-client/ui/components';
import { buildItemCardStats } from '@sorrem-client/ui/shared/item-card-stats';
import type { Equipment, EquipmentPiece, StartingEquipmentOption } from '@sorrem/shared';
import { OptionTile } from '../OptionTile';
import styles from './StartingEquipmentPicker.module.scss';

interface StartingEquipmentPickerProps {
    /** The choices (see `STARTING_ARMOR_OPTIONS` / `STARTING_WEAPON_OPTIONS`). */
    options: readonly StartingEquipmentOption[];
    /** Each option as the server equips it (`GET /api/starting-equipment`): its name and stats. Empty until it loads. */
    catalog: readonly Equipment[];
    /** The asset catalog, for an option's name before `catalog` loads. */
    equipmentPieces: readonly EquipmentPiece[];
    /** The body being created, whose icon each option shows (outfit armor has one per body). */
    characterModelId: string;
    /** The picked option's `itemDefinitionId`. */
    selectedId: string;
    onSelect: (itemDefinitionId: string) => void;
    /** The stats (item card keys, e.g. `damage`) the line under the tiles shows for the pick; all of them when omitted. The hover preview always shows all. */
    summaryStatKeys?: readonly string[];
    isDisabled?: boolean;
}

/** An item's stats (only `keys`, when given) as one line, the way its item card labels them: `Dodge +1, Resistance +1`. */
function formatStats(item: Equipment | undefined, keys?: readonly string[]): string {
    if (!item) {
        return '';
    }
    return buildItemCardStats(item, [])
        .filter((stat) => !keys || keys.includes(stat.key))
        .map((stat) => `${stat.label} ${stat.isBonus && Number(stat.value) > 0 ? `+${stat.value}` : stat.value}`)
        .join(', ');
}

/**
 * Character creation's starting armor (worn as the outfit) or weapon picker:
 * each option as its item icon, the same tiles the appearance pickers use,
 * with the pick's stats in a quiet line below and any option's stats in its
 * hover preview.
 */
export function StartingEquipmentPicker({
    options,
    catalog,
    equipmentPieces,
    characterModelId,
    selectedId,
    onSelect,
    summaryStatKeys,
    isDisabled = false,
}: StartingEquipmentPickerProps): JSX.Element {
    const findItem = (itemDefinitionId: string): Equipment | undefined => catalog.find((item) => item.itemDefinitionId === itemDefinitionId);
    const selectedStats = formatStats(findItem(selectedId), summaryStatKeys);

    return (
        <div className={styles.picker}>
            <div className={styles.options}>
                {options.map((option) => {
                    const item = findItem(option.itemDefinitionId);
                    const name = item?.name ?? equipmentPieces.find((piece) => piece.id === option.modelId)?.name ?? option.modelId;
                    const stats = formatStats(item);
                    return (
                        <OptionTile
                            key={option.itemDefinitionId}
                            label={name}
                            renderImage={(className) => (
                                <ItemIcon modelId={option.modelId} characterModelId={characterModelId} className={className} />
                            )}
                            previewDetails={stats || undefined}
                            isSelected={option.itemDefinitionId === selectedId}
                            isDisabled={isDisabled}
                            onClick={() => onSelect(option.itemDefinitionId)}
                        />
                    );
                })}
            </div>
            {selectedStats && <p className={styles.stats}>{selectedStats}</p>}
        </div>
    );
}
